import { Component, computed, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, Event as NavigationEvent, NavigationEnd } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import { BreakpointService } from '@shure/cloud/shared/services/media-breakpoints';
import { APP_ENVIRONMENT, AppEnvironment, getShureCloudUri } from '@shure/cloud/shared/utils/config';

import { footerUrls } from './footer-urls';
import { getFormattedUrl } from './footer-utils';
import { FooterLink } from './footer.model';

@Component({
	selector: 'sh-footer',
	templateUrl: './footer.component.html',
	styleUrls: ['./footer.component.scss']
})
export class FooterComponent implements OnInit, OnDestroy {
	@Output() public contactUs: EventEmitter<boolean> = new EventEmitter<boolean>();
	public activeFooterLinksList!: FooterLink[];
	public localeCode: string = localStorage.getItem('locale') || 'en';

	/**
	 *  Work around for the translatation Verify step. Can't find keys
	 *  within selectTranslate, but will find them in the this t construct
	 *		t(cloud.shared.footer.do-not-sell-my-data)
	 *		t(cloud.shared.footer.change-cookie-preference)
	 *		t(cloud.shared.footer.contact)
	 *		t(cloud.shared.footer.shure-cloud-faq)
	 *		t(cloud.shared.footer.privacy-policy)
	 *		t(cloud.shared.footer.terms-and-conditions)
	 *		t(cloud.shared.footer.imprint)
	 *		t(cloud.shared.footer.cookie-policy)
	 */

	// using selectTranslate here instead of the normal translate because when the CDM app
	// uses this component, it apparently renders before the <lang>.json file is downloaded
	// and the translations are not correct. By using selectTranslate, this component
	// will adapt to any language changes automatically.
	private footerLabels = {
		cookieBotPart1: toSignal(this.translocoService.selectTranslate('cloud.shared.footer.do-not-sell-my-data')),
		cookieBotPart2: toSignal(this.translocoService.selectTranslate('cloud.shared.footer.change-cookie-preference')),
		contact: toSignal(this.translocoService.selectTranslate('cloud.shared.footer.contact')),
		shureCloudFaq: toSignal(this.translocoService.selectTranslate('cloud.shared.footer.shure-cloud-faq')),
		privayPolicy: toSignal(this.translocoService.selectTranslate('cloud.shared.footer.privacy-policy')),
		termsAndConditiosn: toSignal(this.translocoService.selectTranslate('cloud.shared.footer.terms-and-conditions')),
		imprint: toSignal(this.translocoService.selectTranslate('cloud.shared.footer.imprint')),
		cookiePolicy: toSignal(this.translocoService.selectTranslate('cloud.shared.footer.cookie-policy'))
	};

	public footerLinksList: FooterLink[] = [
		{
			label: this.footerLabels.contact,
			id: 'contact-us',
			url: getFormattedUrl('CONTACT_US')
		},
		{
			label: this.footerLabels.shureCloudFaq,
			id: 'faq',
			url: footerUrls.SHURE_CLOUD_FAQ
		},
		{
			label: this.footerLabels.privayPolicy,
			id: 'privacy',
			url: getFormattedUrl('PRIVACY_POLICY')
		},
		{
			label: this.footerLabels.termsAndConditiosn,
			id: 'terms',
			url: getFormattedUrl('TERMS_CONDITIONS')
		},
		{
			label: this.footerLabels.imprint,
			id: 'imprint',
			url: footerUrls.IMPRINT
		},
		{
			label: this.footerLabels.cookiePolicy,
			id: 'cookie-policy',
			url: getFormattedUrl('COOKIE_POLICY')
		},
		{
			label: computed<string>(() => {
				return this.footerLabels.cookieBotPart1() + '/' + this.footerLabels.cookieBotPart2();
			}),
			id: 'cookie',
			url: 'javascript: Cookiebot.renew()'
		}
	];
	private destroy$: Subject<void> = new Subject<void>();
	public appVersion!: string;
	public currentYear: number = new Date().getFullYear();
	public readonly isXSmallDevice = this.breakpointService.isLteXSmall;
	public readonly isSmallDevice = this.breakpointService.isLteSmall;
	constructor(
		@Inject(APP_ENVIRONMENT) public appEnv: AppEnvironment,
		private translocoService: TranslocoService,
		private router: Router,
		private readonly breakpointService: BreakpointService
	) {}

	public ngOnInit(): void {
		this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: NavigationEvent) => {
			if (event instanceof NavigationEnd) {
				this.setFooterLinks(event.url);
			}
		});
		this.setFooterLinks(this.router.url);
	}

	/**
	 * Handle click event for the footer links and  redirect to page
	 * @param footerLink - footerLink which is clicked
	 */
	public onFooterLinkClick(footerLink: FooterLink): void {
		if (footerLink.id === 'cookie' && footerLink.url) {
			window.location.href = footerLink.url;
		} else if (footerLink.id === 'imprint') {
			this.routeToImprint();
		} else if (footerLink.id === 'contact-us') {
			this.contactUs.emit(true);
			window.open(footerLink.url);
		} else window.open(footerLink.url);
	}

	/**
	 * Route to correct imprint link
	 */
	public routeToImprint(): void {
		const shureCloudUri = getShureCloudUri(window.location.origin, this.appEnv.shureCloudUriMap) ?? '';
		window.open(`${shureCloudUri}/imprint`);
	}

	/**
	 * Handle footer links in all pages
	 * @param url - active url in the tab
	 */
	public setFooterLinks(url: string): void {
		if (url === '/home') {
			this.activeFooterLinksList = this.footerLinksList.slice(2);
		} else {
			this.activeFooterLinksList = this.footerLinksList;
		}
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
